import { useCallback } from "react";
import useSWR from "swr";
import { FileInfo, Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { CarDocument, RenameDocument, ShowCarDocumentToUser } from "./types";

export const useMyCarDocumentsApi = () => {
  const api = useApi();

  const useMyCarDocuments = useCallback(
    (query: PaginationQuery, signal: AbortSignal, carId: string, isTeam: boolean) => {
      const uri = isTeam ? "my_team_cars" : "my_cars";
      const url = buildUrl(`/${uri}/${carId}/documents`, query);
      return useSWR<Paginated<CarDocument>, CustomError, string>(url, (url) =>
        api.get<Paginated<CarDocument>>({ url, signal }),
      );
    },
    [api],
  );

  return {
    useMyCarDocuments,
  };
};

export const useCarDocumentsApi = () => {
  const api = useApi();

  const useCarDocuments = useCallback(
    (query: PaginationQuery, signal: AbortSignal, carId?: string) => {
      const url = buildUrl(carId ? `/admin/cars/${carId}/documents` : "/admin/car_documents", query);
      return useSWR<Paginated<CarDocument>, CustomError, string>(url, (url) =>
        api.get<Paginated<CarDocument>>({ url, signal }),
      );
    },
    [api],
  );

  const deleteCarDocument = useCallback(
    async (documentId: string, carId?: string): Promise<void> => {
      const url = `/admin/car_documents/${documentId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/car_documents`, `/admin/cars/${carId}/documents`]);
      });
    },
    [api],
  );

  const insertCarDocument = useCallback(
    async (file: File, overWrite?: boolean, carId?: string): Promise<void> => {
      const formData = new FormData();
      formData.append("file", file);

      await api.put<FormData>(`/admin/cars/${carId}/documents${overWrite ? "?overwrite=1" : ""}`, formData);
      api.mutateCache([`/admin/cars/${carId}/documents`, "/admin/car_documents"]);
    },
    [api],
  );

  const insertCarDocuments = useCallback(
    async (files: File[], carId?: string): Promise<void> => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file, file.name);
      });
      await api.postNoResult<FormData>(`/admin/cars/${carId}/documents`, formData);
      api.mutateCache([`/admin/cars/${carId}/documents`, "/admin/car_documents"]);
    },
    [api],
  );

  const renameCarDocument = useCallback(
    async (docId: string, body: RenameDocument, carId?: string): Promise<void> => {
      await api.put<RenameDocument>(`/admin/car_documents/${docId}`, body);
      api.mutateCache([`/admin/cars/${carId}/documents`, "/admin/car_documents"]);
    },
    [api],
  );

  const updateCarDocumentVisibleToUser = useCallback(
    async (docId: string, body: ShowCarDocumentToUser, carId?: string): Promise<void> => {
      await api.put<ShowCarDocumentToUser>(`/admin/car_documents/${docId}/show_to_user`, body);
      api.mutateCache([
        `/admin/cars/${carId}/documents`,
        "/admin/car_documents",
        `/my_cars/${carId}/documents`,
        `/my_team_cars/${carId}/documents`,
      ]);
    },
    [api],
  );

  const getCarDocument = useCallback(
    (docID: string, signal: AbortSignal) => {
      const url = `/admin/car_documents/${docID}`;
      const r = api.get<FileInfo>({ url, signal });
      return r;
    },
    [api],
  );

  return {
    useCarDocuments,
    deleteCarDocument,
    insertCarDocument,
    insertCarDocuments,
    getCarDocument,
    renameCarDocument,
    updateCarDocumentVisibleToUser,
  };
};
